import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { isoToVerbalDateTime } from "../../utils/dateFunction"
import { VisitLogEdit } from "./VisitLogEdit"
import { VisitorApi } from "./visitor-api"
import { DashboardStats, FollowUpDto, VisitLog, VisitPurpose } from "./visitor-dto"
import PermissionGuard from "../../components/PermissionGuard"

export const VisitorDashboard = () => {


    const navigate = useNavigate()
    const [recentVisitors, setRecentVisitors] = useState<VisitLog[] | null>()
    const [showAddVisitor, setShowAddVisitor] = useState(false)
    const [editVisitLog, setEditVisitLog] = useState<VisitLog>()
    const [pendingFollowUps, setPendingFollowUps] = useState<FollowUpDto[]>([])
    const [stats, setStats] = useState<DashboardStats>()
    const [loading, setLoading] = useState(true)

    function loadVisitLogs() {
        setLoading(true)
        VisitorApi.loadVisitLogs(1, 10).then((data) => {
            // New API returns { success: true, data: VisitLog[], pagination: {...} }
            const responseData = data?.data
            if (responseData?.success && responseData?.data) {
                setRecentVisitors(responseData.data)
            } else if (responseData?.data) {
                // Fallback for old format
                setRecentVisitors(responseData.data)
            }
            setLoading(false)
        }).catch(() => {
            setLoading(false)
        })
    }

    function loadPendingFollowups() {
        VisitorApi.getPendingFollowUps().then((data) => {
            setPendingFollowUps((data?.data as any).data)
        })
    }

    function loadDashboardStats() {
        VisitorApi.getDashboard().then((data) => {
            setStats((data?.data as any).data)
        })
    }


    useEffect(() => {
        loadVisitLogs()
        loadPendingFollowups()
        loadDashboardStats()
    }, [])

    function getVisitDetailsUrl(visitor: VisitLog): import("react-router-dom").To {

        if (visitor.is_member) {
            return `/visitors/member_id/${visitor.member?.id}`
        } else {
            return `/visitors/visitor_id/${visitor.visitor?.id}`
        }

    }

    function handleCheckout(id: number) {
        VisitorApi.checkoutVisitor(id).then(d => {
            loadVisitLogs()
        }).catch(e => {
            alert(e)
        })
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
            <div className="max-w-[1600px] mx-auto">
                {/* Header Section */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                                Visitor Management Dashboard
                            </h1>
                            <p className="text-gray-600 dark:text-gray-300">
                                Manage visitor information, track follow-ups, and connect with new members
                            </p>
                        </div>
                        <PermissionGuard permission={["visitor.create", "visitor.manage"]}>
                            <button 
                                className="btn-primary flex items-center gap-2 px-6 py-3 shadow-lg"
                                onClick={() => setShowAddVisitor(true)}
                            >
                                <i className="fa-solid fa-plus"></i>
                                <span>Log Visit</span>
                            </button>
                        </PermissionGuard>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 dark:border-opacity-80 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                New Visitors
                            </h3>
                            <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 p-2 rounded-lg">
                                <i className="fa-solid fa-user-plus"></i>
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            {stats?.visitsThisMonth || 0}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="text-green-600 dark:text-green-400">
                                <i className="fa-solid fa-arrow-up mr-1"></i>
                                {stats?.visitsChangePercentage || 0}%
                            </span>
                            <span className="ml-1">from last month</span>
                        </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 dark:border-opacity-80 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                Pending Follow-ups
                            </h3>
                            <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 p-2 rounded-lg">
                                <i className="fa-solid fa-clipboard-list"></i>
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            {stats?.pendingFollowUpsCount || 0}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="text-yellow-600 dark:text-yellow-400">
                                <i className="fa-solid fa-arrow-up mr-1"></i>
                                {stats?.pendingFollowUpsChange || 0}
                            </span>
                            <span className="ml-1">since yesterday</span>
                        </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 dark:border-opacity-80 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                Completed Follow-ups
                            </h3>
                            <span className="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 p-2 rounded-lg">
                                <i className="fa-solid fa-check-circle"></i>
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            {stats?.completedFollowUpsThisMonth || 0}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="text-green-600 dark:text-green-400">
                                <i className="fa-solid fa-arrow-up mr-1"></i>
                                {stats?.completedFollowUpsChangePercentage || 0}%
                            </span>
                            <span className="ml-1">from last week</span>
                        </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 dark:border-opacity-80 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                Conversion Rate
                            </h3>
                            <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 p-2 rounded-lg">
                                <i className="fa-solid fa-chart-line"></i>
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            {stats?.conversionRateThisMonth || 0}%
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="text-green-600 dark:text-green-400">
                                <i className="fa-solid fa-arrow-up mr-1"></i>
                                {stats?.conversionRateChange || 0}%
                            </span>
                            <span className="ml-1">from last month</span>
                        </p>
                    </div>
                </div>

                {/* Recent Visitors Section */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                                Recent Visitors
                            </h2>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Latest visitor entries and check-ins
                            </p>
                        </div>
                        <button 
                            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 flex items-center gap-1 transition-colors"
                            onClick={() => navigate('/visitors/list')}
                        >
                            View All
                            <i className="fa-solid fa-arrow-right"></i>
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                            <div className="flex flex-col items-center gap-4">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                                <p className="text-gray-600 dark:text-gray-300">Loading visitors...</p>
                            </div>
                        </div>
                    ) : !recentVisitors || recentVisitors.length === 0 ? (
                        <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                            <div className="flex flex-col items-center gap-4">
                                <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                    <i className="fa-solid fa-users text-3xl text-gray-400 dark:text-gray-500"></i>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    No visitors found
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300">
                                    Get started by logging your first visit
                                </p>
                                <PermissionGuard permission={["visitor.create", "visitor.manage"]}>
                                    <button
                                        onClick={() => setShowAddVisitor(true)}
                                        className="btn-primary mt-4 flex items-center gap-2 px-6 py-3"
                                    >
                                        <i className="fa-solid fa-plus"></i>
                                        <span>Log Visit</span>
                                    </button>
                                </PermissionGuard>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 dark:border-opacity-80 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-700">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                Visitor
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                Entry Date Time
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                Exit Date Time
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                Purpose
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {recentVisitors.map((visitLog, index) => (
                                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 h-10 w-10">
                                                            <img 
                                                                src="https://api.dicebear.com/7.x/notionists/svg?scale=200&seed=456" 
                                                                alt="Visitor avatar" 
                                                                className="h-10 w-10 rounded-full border-2 border-gray-200 dark:border-gray-600" 
                                                            />
                                                        </div>
                                                        <div className="ml-4">
                                                            <div 
                                                                onClick={() => navigate(getVisitDetailsUrl(visitLog))} 
                                                                className="text-sm font-medium text-gray-900 dark:text-white cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                                                            >
                                                                {visitLog.member?.name || visitLog.visitor?.visitor_full_name}
                                                            </div>
                                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                {visitLog.visitor?.phones?.[0] || visitLog.member?.phone}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 dark:text-gray-100">
                                                        {isoToVerbalDateTime(visitLog.entry_time)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {visitLog.exit_time ? (
                                                        <div className="text-sm text-gray-900 dark:text-gray-100">
                                                            {isoToVerbalDateTime(visitLog.exit_time)}
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400 dark:text-gray-500 italic">Not checked out</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                                                        {VisitPurpose[visitLog.purpose]}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button 
                                                            disabled={visitLog.check_out != null} 
                                                            className="p-2 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
                                                            onClick={() => handleCheckout(visitLog.id)}
                                                            title="Checkout"
                                                        >
                                                            <i className="fa-solid fa-sign-out"></i>
                                                        </button>
                                                        <button 
                                                            className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" 
                                                            onClick={() => setEditVisitLog(visitLog)}
                                                            title="Edit"
                                                        >
                                                            <i className="fa-solid fa-user-edit"></i>
                                                        </button>
                                                        <button 
                                                            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" 
                                                            onClick={() => navigate(getVisitDetailsUrl(visitLog))}
                                                            title="View Details"
                                                        >
                                                            <i className="fa-solid fa-eye"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Upcoming Follow-ups Section */}
                {pendingFollowUps && pendingFollowUps.length > 0 && (
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                                    Upcoming Follow-ups
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 text-sm">
                                    Pending follow-up tasks and reminders
                                </p>
                            </div>
                            <button 
                                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 flex items-center gap-1 transition-colors"
                            >
                                View All
                                <i className="fa-solid fa-arrow-right"></i>
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {pendingFollowUps.filter((_, i) => i < 3).map((f, index) => (
                                <div 
                                    key={index}
                                    className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 dark:border-opacity-80 p-6 hover:shadow-xl transition-shadow"
                                >
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center gap-3">
                                            <img 
                                                src="https://api.dicebear.com/7.x/notionists/svg?scale=200&seed=456" 
                                                alt="Visitor avatar" 
                                                className="h-10 w-10 rounded-full border-2 border-gray-200 dark:border-gray-600" 
                                            />
                                            <span className="text-base font-semibold text-gray-900 dark:text-white">
                                                {f.visitor?.visitor_full_name}
                                            </span>
                                        </div>
                                        <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs font-semibold px-2 py-1 rounded-full">
                                            Due
                                        </span>
                                    </div>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2">
                                        {f.notes}
                                    </p>
                                    <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                            {isoToVerbalDateTime(f.next_proposed_contact_date)}
                                        </span>
                                        <div className="flex gap-2">
                                            <button 
                                                className="p-2 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                                title="Complete"
                                            >
                                                <i className="fa-solid fa-check"></i>
                                            </button>
                                            <button 
                                                className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                title="Reschedule"
                                            >
                                                <i className="fa-solid fa-calendar-alt"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {showAddVisitor && (
                <VisitLogEdit 
                    onClose={() => setShowAddVisitor(false)} 
                    onSuccess={() => {
                        setShowAddVisitor(false)
                        loadVisitLogs()
                        loadDashboardStats()
                    }} 
                />
            )}

            {editVisitLog && (
                <VisitLogEdit 
                    visitLog={editVisitLog} 
                    onClose={() => setEditVisitLog(undefined)} 
                    onSuccess={() => {
                        setEditVisitLog(undefined)
                        loadVisitLogs()
                        loadDashboardStats()
                    }} 
                />
            )}
        </div>
    )
}
